import { useCreateAdmin, useUpdateUser } from "@/hooks/useUser";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  FormControl,
  FormItem,
  FormMessage,
  FormField,
  FormLabel,
  Form,
} from "../ui/form";
import { Button } from "../ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanies } from "@/hooks/useCompanies";
import { Loader2 } from "lucide-react";
import type { UserType, UpdateUserData } from "@/types/user.type";
import { Switch } from "../ui/switch";
import { useDebounce } from "use-debounce";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils"; // helper shadcn pour les classes conditionnelles

const adminSchema = z.object({
  firstName: z.string().min(3, "Le prénom doit contenir au moins 3 caractères"),
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().min(1, "L'email est requis").email("Email invalide"),
  password: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 6,
      "Le mot de passe doit contenir au moins 6 caractères"
    ),
  confirmPassword: z.string().optional(),
  companyId: z.string().optional(),
  isActive: z.boolean().optional(),
}).refine((data) => {
  if(data?.password) {
    return data?.password === data?.confirmPassword;
  }
  return true;
}, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"], // L'erreur s'affichera sur le champ confirmPassword
});

type AdminFormValues = z.infer<typeof adminSchema>;

interface AdminFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  admin?: UserType | null;
}

export const AdminFormDialog = ({
  open,
  onOpenChange,
  onSuccess,
  admin,
}: AdminFormDialogProps) => {
  const { user: authUser } = useAuth();
  const isSuperuser = authUser?.role === "SUPERUSER";
  const isEditing = !!admin;
  const [companySearch, setCompanySearch] = useState<string>("");
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false);
  const [debouncedCompanySearch] = useDebounce(companySearch, 400); // débounce pour éviter les requêtes multiples

  const defaultValues = useMemo<AdminFormValues>(
    () => ({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      companyId: isSuperuser ? "" : authUser?.companyId ?? "", // si on est superuser, on ne peut pas sélectionner une entreprise, sinon on sélectionne l'entreprise de l'utilisateur connecté
      isActive: true,
    }),
    [authUser?.companyId, isSuperuser] // Dépendances pour le recalcul des valeurs par défaut
  );

  const { mutate: createAdminMutation, isPending: isCreatingAdmin } =
    useCreateAdmin();
  const { mutate: updateAdminMutation, isPending: isUpdatingAdmin } =
    useUpdateUser();

  // on crée le formulaire avec les valeurs par défaut
  const form = useForm<AdminFormValues>({
    resolver: zodResolver(adminSchema),
    defaultValues,
  });

  // ic on récupère les valeurs du formulaire
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = form;

  // si on est en mode édition, on récupère les valeurs du formulaire
  useEffect(() => {
    if (admin) {
      reset({
        firstName: admin.firstName || "",
        lastName: admin.lastName || "",
        email: admin.email || "",
        password: "",
        companyId: admin.companyId || "",
        isActive: admin.isActive ?? true,
      });
    } else {
      reset(defaultValues);
    }
  }, [admin, reset, defaultValues, open]);

  const { data: companiesData, isFetching: isFetchingCompanies } = useCompanies(
    isSuperuser
      ? {
          limit: 2,
          search: debouncedCompanySearch || undefined,
          isActive: true,
        }
      : undefined,
    {
      enabled: isSuperuser && open, // requête exécutée uniquement si superuser + modal ouverte
    }
  );

  const companyOptions = companiesData?.companies || [];
  // on récupère le nom de l'entreprise courante depuis le formulaire ou l'admin si superuser
  const currentCompanyName =
    companyOptions.find((company) => company.id === form.getValues("companyId")) // getValues pour récupérer la valeur du formulaire
      ?.name ??
    admin?.company?.name ?? // récupérer le nom de l'entreprise de l'admin si superuser
    "Sélectionne une entreprise"; // si aucune entreprise n'est trouvée, on affiche "Sélectionne une entreprise"

  const onSubmit = (data: AdminFormValues) => {
    if (isEditing && admin?.id) {
      const { password, confirmPassword, ...rest } = data; // on récupère les valeurs du formulaire sans le password et le confirmPassword

      const updateData: UpdateUserData = { ...rest, role: "ADMIN" }; // on crée un objet avec les valeurs du formulaire et le role ADMIN

      if (password && password.trim() !== "") {
        updateData.password = password; // on ajoute le password si il est rempli
      }

      updateAdminMutation(
        {
          id: admin.id,
          data: updateData
        },
        {
          onSuccess: () => {
            toast.success("Administrateur modifié avec succès", {
              style: {
                background: "#10b981",
                color: "white",
                border: "none",
              },
            });
            form.reset(defaultValues);
            onOpenChange(false);
            onSuccess?.();
          },
          onError: (error: Error) => {
            toast.error(
              error.message ||
                "Une erreur est survenue lors de la modification de l'administrateur",
              {
                style: {
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                },
              }
            );
          },
        }
      );
      return;
    }

    const payload = {
      ...data,
      role: "ADMIN" as const,
      isActive: true,
    };

    createAdminMutation(payload, {
      onSuccess: () => {
        toast.success("Administrateur créé avec succès", {
          style: {
            background: "#10b981",
            color: "white",
            border: "none",
          },
        });
        form.reset(defaultValues);
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (error: Error) => {
        toast.error(
          error.message ||
            "Une erreur est survenue lors de la création de l'administrateur",
          {
            style: {
              background: "#ef4444",
              color: "white",
              border: "none",
            },
          }
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-1rem)] sm:max-w-[525px] md:max-w-[600px] p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {isEditing ? "Modifier un administrateur" : "Nouvel administrateur"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm sm:text-base">Prénom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm sm:text-base">Nom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm sm:text-base">Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="admin@entreprise.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2 w-full"> 
              <FormField
                control={control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="text-sm sm:text-base">
                      Mot de passe
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Au moins 6 caractères"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem> 
                )}
              />

              <FormField
                control={control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="text-sm sm:text-base">
                      Confirmer le mot de passe
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Confirmer le mot de passe"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            


            {isSuperuser && (
              <FormField
                control={control}
                name="companyId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm sm:text-base">
                      Entreprise
                    </FormLabel>
                    <Popover
                      open={companyPopoverOpen}
                      onOpenChange={(nextOpen) => {
                        setCompanyPopoverOpen(nextOpen);
                        if (!nextOpen) {
                          setCompanySearch("");
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="w-full justify-between"
                          >
                            {currentCompanyName}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[var(--radix-popover-trigger-width)] p-0"
                        align="start"
                      >
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Rechercher une entreprise..."
                            value={companySearch}
                            onValueChange={(value) => {
                              setCompanySearch(value);
                            }}
                          />
                          <CommandList>
                            {isFetchingCompanies ? (
                              <div className="py-3 text-center text-sm text-muted-foreground">
                                Chargement...
                              </div>
                            ) : companyOptions.length === 0 ? (
                              <CommandEmpty>
                                Aucune entreprise trouvée.
                              </CommandEmpty>
                            ) : (
                              companyOptions.map((company) => (
                                <CommandItem
                                  key={company.id}
                                  value={company.name}
                                  onSelect={() => {
                                    field.onChange(company.id);
                                    setCompanyPopoverOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      company.id === field.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {company.name}
                                </CommandItem>
                              ))
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isEditing && (
              <FormField
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-col sm:flex-row sm:items-center sm:justify-between rounded-lg border p-3 sm:p-4 gap-3 sm:gap-0">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm sm:text-base">
                        Statut
                      </FormLabel>
                      <div className="text-xs sm:text-sm text-muted-foreground">
                        L'administrateur est active et peut utiliser le système
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
            <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-2">
              <Button
                type="submit"
                disabled={
                  isEditing ? !isDirty || isUpdatingAdmin : isCreatingAdmin
                }
                className="w-full sm:w-auto order-1 sm:order-2"
              >
                {isCreatingAdmin || isUpdatingAdmin ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    En cours...
                  </>
                ) : isEditing ? (
                  "Modifier"
                ) : (
                  "Créer"
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Annuler
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
